/**
 * Color utility helpers used by Preferences and TagInput.
 * - hslToHex: convert HSL to hex
 * - hexToHsl: convert hex to HSL (returns { h, s, l })
 * - generateDifferentiatedColors: assign well-separated colors to list of items
 * - pickColorForNewTag: choose a single color highly differentiated from a set of existing colors
 */

export function hslToHex(h: number, s: number, l: number) {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToHsl(hex?: string) {
  if (!hex) return null;
  try {
    const normalized = hex.replace("#", "");
    const r = parseInt(normalized.substring(0, 2), 16) / 255;
    const g = parseInt(normalized.substring(2, 4), 16) / 255;
    const b = parseInt(normalized.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h = h * 60;
    }
    return { h, s: s * 100, l: l * 100 };
  } catch {
    return null;
  }
}

/**
 * Assigns well-separated colors to items using HSL hue spacing.
 */
export function generateDifferentiatedColors<T extends { id: string }>(
  items: T[],
) {
  const n = items.length;
  const result: Record<string, string> = {};
  if (n === 0) return result;
  const hueStep = 360 / n;
  const start = Math.random() * 360;
  for (let i = 0; i < n; i++) {
    const hue = (start + i * hueStep) % 360;
    const saturation = 65 + Math.random() * 20; // 65-85%
    const lightness = 45 + Math.random() * 10; // 45-55%
    result[items[i].id] = hslToHex(hue, saturation, lightness);
  }
  return result;
}

/**
 * Choose a single color that is highly differentiated from the provided existing colors.
 * Strategy:
 *  - Convert existing colors to hues (ignore invalid)
 *  - Find the largest angular gap on the hue wheel and pick the midpoint + small jitter
 *  - Fallback to random hue
 */
export function pickColorForNewTag(existingHexColors: (string | undefined)[]) {
  const hues: number[] = [];
  for (const h of existingHexColors) {
    const parsed = hexToHsl(h ?? undefined);
    if (parsed && !Number.isNaN(parsed.h)) hues.push(parsed.h);
  }

  if (hues.length === 0) {
    // No existing colors - pick a random pleasing hue
    const hue = Math.floor(Math.random() * 360);
    return hslToHex(hue, 75, 50);
  }

  // Normalize and sort
  const sorted = Array.from(
    new Set(hues.map((x) => Math.round(x * 100) / 100)),
  ).sort((a, b) => a - b);

  // Find largest gap between consecutive hues (circular)
  let maxGap = -1;
  let gapIndex = 0;
  for (let i = 0; i < sorted.length; i++) {
    const a = sorted[i];
    const b =
      sorted[(i + 1) % sorted.length] + (i + 1 === sorted.length ? 360 : 0);
    const gap = b - a;
    if (gap > maxGap) {
      maxGap = gap;
      gapIndex = i;
    }
  }

  const a = sorted[gapIndex];
  const b =
    sorted[(gapIndex + 1) % sorted.length] +
    (gapIndex + 1 === sorted.length ? 360 : 0);
  let pick = (a + b) / 2;
  pick = pick % 360;
  // add slight jitter so it's not exactly halfway every time
  pick = (pick + (Math.random() - 0.5) * Math.min(20, maxGap / 2) + 360) % 360;

  const saturation = 70 + Math.random() * 10;
  const lightness = 48 + Math.random() * 6;
  return hslToHex(pick, saturation, lightness);
}
